import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cutDendrogram, defaultThreshold, maxHeight } from './discoveryCut';

// DiscoveryReviewPanel — WP-D3b.3. Renders the stored discovery dendrogram
// with a threshold slider (re-cut client-side, instant), clusters sorted by
// size with the medoid response as each cluster's exemplar, an explicit
// Unclustered pool (nothing silently dropped — Σ sizes + unclustered = n,
// shown), and per-cluster name inputs. "Adopt as categories" hands the named
// selection up to the canvas, which PUTs response-categories and refreshes
// the survey structure. Deterministic end-to-end; naming is the author's.

export default function DiscoveryReviewPanel({ discovery, textBySession, onAdopt, busy, error, saveSlot }) {
    const payload = discovery?.result || null;
    const merges = useMemo(
        () => (Array.isArray(payload?.merges) ? payload.merges : []),
        [payload]
    );
    const sessions = useMemo(
        () => (Array.isArray(payload?.sessions) ? payload.sessions : []),
        [payload]
    );

    const hMax = useMemo(() => maxHeight(merges), [merges]);
    const [threshold, setThreshold] = useState(() => defaultThreshold(merges));
    // Selection + names keyed by medoid leaf index so slider tweaks that keep
    // a cluster's exemplar also keep the author's typed name.
    const [names, setNames] = useState({});
    const [deselected, setDeselected] = useState({});
    const [expanded, setExpanded] = useState({});
    const [localError, setLocalError] = useState(null);

    const cut = useMemo(() => cutDendrogram(payload, threshold), [payload, threshold]);

    const leafText = useCallback((leafIdx) => {
        const sid = sessions[leafIdx];
        const t = textBySession?.get?.(sid);
        return t != null ? String(t) : '(response text unavailable)';
    }, [sessions, textBySession]);

    const clusterName = useCallback((c) => {
        const typed = names[c.medoid];
        if (typed != null) return typed;
        const t = leafText(c.medoid);
        return t.length > 60 ? t.slice(0, 57) + '…' : t;
    }, [names, leafText]);

    const selectedClusters = cut.clusters.filter(c => !deselected[c.medoid]);

    const adopt = useCallback(() => {
        setLocalError(null);
        const cats = [];
        const seen = new Set();
        for (const c of selectedClusters) {
            const name = String(clusterName(c)).trim();
            if (!name) {
                setLocalError('Every selected cluster needs a category name.');
                return;
            }
            const k = name.toLowerCase();
            if (seen.has(k)) {
                setLocalError(`Duplicate category name: "${name}". Names must be unique.`);
                return;
            }
            seen.add(k);
            const medoidText = leafText(c.medoid);
            const cat = { summary: name };
            // The medoid answer (real respondent language) seeds the category's
            // phrasings so the classifier's mean vector starts on-data.
            if (medoidText && medoidText !== name && medoidText !== '(response text unavailable)') {
                cat.phrasings = [medoidText];
            }
            cats.push(cat);
        }
        if (cats.length === 0) {
            setLocalError('Select at least one cluster to adopt.');
            return;
        }
        onAdopt?.(cats);
    }, [selectedClusters, clusterName, leafText, onAdopt]);

    if (!payload || sessions.length === 0) {
        return <div style={{ padding: 16, color: '#5F6368', fontSize: 13 }}>No discovery result to review.</div>;
    }

    const clusteredMass = cut.clusters.reduce((s, c) => s + c.size, 0);

    // Save affordance. Rendered as a plain card (not a sticky bottom bar) so
    // it can live in the right rail under the Summary box, statically visible
    // while the cluster list scrolls. When `saveSlot` (a DOM node) is provided
    // it portals there; otherwise it renders inline (standalone/dev usage).
    const saveCard = (
        <div style={{
            display: 'flex', flexDirection: 'column', gap: 10,
            padding: '14px 16px',
            backgroundColor: '#FFFFFF', border: '1px solid #E8EAED', borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
        }}>
            <button
                onClick={adopt}
                disabled={busy || selectedClusters.length === 0}
                style={{ width: '100%', padding: '10px 16px', fontSize: 14, fontWeight: 600, color: '#FFFFFF', backgroundColor: busy || selectedClusters.length === 0 ? '#9AA0A6' : '#1967D2', border: 'none', borderRadius: 4, cursor: busy ? 'wait' : (selectedClusters.length === 0 ? 'not-allowed' : 'pointer') }}
            >
                {busy ? 'Saving…' : `Save ${selectedClusters.length} categor${selectedClusters.length === 1 ? 'y' : 'ies'} to this survey`}
            </button>
            <span style={{ fontSize: 12, color: '#5F6368', lineHeight: 1.4 }}>
                Writes the ticked clusters as this question's expected response categories. Categorize then activates.
            </span>
        </div>
    );

    return (
        <div style={{ fontFamily: 'var(--font-family-primary)', textAlign: 'left', maxWidth: 860, margin: '0 auto' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#202124', marginBottom: 4 }}>
                Discovered candidate categories
            </div>
            <div style={{ fontSize: 12, color: '#5F6368', marginBottom: 12 }}>
                Grouped by semantic similarity of the answers themselves (deterministic — no AI authorship).
                Drag the slider to merge or split groups, name the ones worth keeping, then adopt.
            </div>

            {/* Similarity slider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: '#5F6368', whiteSpace: 'nowrap' }}>Fewer, broader</span>
                <input
                    type="range"
                    min={0}
                    max={hMax || 1}
                    step={(hMax || 1) / 200}
                    // Slider is inverted vs. distance: right = tighter (smaller threshold).
                    value={(hMax || 1) - threshold}
                    onChange={(e) => setThreshold((hMax || 1) - Number(e.target.value))}
                    style={{ flex: 1, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, color: '#5F6368', whiteSpace: 'nowrap' }}>More, tighter</span>
            </div>

            {/* n reconciliation */}
            <div style={{ fontSize: 12, color: '#5F6368', marginBottom: 12, padding: '6px 10px', backgroundColor: '#FAFBFC', border: '1px solid #E8EAED', borderRadius: 6 }}>
                {cut.clusters.length} cluster{cut.clusters.length === 1 ? '' : 's'} ({clusteredMass}) + unclustered ({cut.unclustered.length}) = n ({cut.n})
            </div>

            {(error || localError) && (
                <div style={{ color: '#C62828', fontSize: 13, marginBottom: 10 }}>{error || localError}</div>
            )}

            {/* Cluster list — flows naturally; the AnalysisTab left column is
                the single scroll region, so this no longer scrolls on its own. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {cut.clusters.map((c) => {
                    const isSel = !deselected[c.medoid];
                    const isOpen = !!expanded[c.medoid];
                    return (
                        <div key={c.medoid} style={{ border: '1px solid #E8EAED', borderRadius: 6, padding: 12, backgroundColor: isSel ? '#FFFFFF' : '#F8F9FA', opacity: isSel ? 1 : 0.7 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <input
                                    type="checkbox"
                                    checked={isSel}
                                    onChange={() => setDeselected(d => ({ ...d, [c.medoid]: !d[c.medoid] }))}
                                    style={{ cursor: 'pointer' }}
                                />
                                <input
                                    type="text"
                                    value={clusterName(c)}
                                    onChange={(e) => setNames(m => ({ ...m, [c.medoid]: e.target.value }))}
                                    disabled={!isSel || busy}
                                    style={{ flex: 1, minWidth: 220, fontSize: 13, padding: '5px 8px', border: '1px solid #DADCE0', borderRadius: 4, color: '#202124' }}
                                />
                                <span style={{ fontSize: 12, color: '#5F6368', whiteSpace: 'nowrap' }}>{c.size} response{c.size === 1 ? '' : 's'}</span>
                                <button onClick={() => setExpanded(x => ({ ...x, [c.medoid]: !x[c.medoid] }))} style={btnGhost}>
                                    {isOpen ? 'Hide' : 'Show'} members
                                </button>
                            </div>
                            <div style={{ fontSize: 12, color: '#5F6368', marginTop: 6, fontStyle: 'italic' }}>
                                “{leafText(c.medoid)}”
                            </div>
                            {isOpen && (
                                <div style={{ marginTop: 8, borderTop: '1px solid #F1F3F4', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                                    {c.members.map(m => (
                                        <div key={m} style={{ fontSize: 12, color: m === c.medoid ? '#202124' : '#5F6368' }}>
                                            {m === c.medoid ? '★ ' : '· '}{leafText(m)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Unclustered pool — explicit, never silently dropped */}
            {cut.unclustered.length > 0 && (
                <div style={{ border: '1px dashed #DADCE0', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#5F6368' }}>Unclustered — {cut.unclustered.length}</span>
                        <button onClick={() => setExpanded(x => ({ ...x, __unclustered__: !x.__unclustered__ }))} style={btnGhost}>
                            {expanded.__unclustered__ ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    <div style={{ fontSize: 11, color: '#9AA0A6', marginTop: 4 }}>
                        Answers with no near neighbors at this cut. They classify as Other/Ambiguous later — nothing is dropped.
                    </div>
                    {expanded.__unclustered__ && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                            {cut.unclustered.map(m => (
                                <div key={m} style={{ fontSize: 12, color: '#5F6368' }}>· {leafText(m)}</div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Save affordance lives in the right rail (portaled under the
                Summary box). Falls back to inline when no slot is provided. */}
            {saveSlot === undefined ? saveCard : (saveSlot ? createPortal(saveCard, saveSlot) : null)}
        </div>
    );
}

const btnGhost = { fontSize: 12, padding: '4px 10px', border: '1px solid #DADCE0', borderRadius: 4, background: '#FFFFFF', color: '#3C4043', cursor: 'pointer' };
